/**
 * DualScopeScore - vue dashboard a deux perimetres alignes verticalement :
 *
 *   [ Score M365 ]  +  [ Score Azure ]  =  [ Score Global ]
 *   [ Familles M365 ]              |   [ Familles Azure ]
 *
 * - Score global = moyenne ponderee par le nombre de controles evalues
 *   (pass + warning + fail) de chaque perimetre.
 * - Respecte les toggles locaux (useScanVisibility) : une colonne masquee
 *   n est ni affichee ni comptee dans le score global.
 * - M365 : computeScoreBreakdown(scan).byFamily (scores 0..100).
 * - Azure : % de controles conformes par famille (pass / total evalues).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Card, Caption1, Body1, tokens,
} from '@fluentui/react-components';
import {
  ResponsiveContainer, RadialBar, RadialBarChart, PolarAngleAxis,
} from 'recharts';
import { BrandIcon } from './BrandIcon';
import { useScanVisibility } from '../hooks/useScanVisibility';
import { computeScoreBreakdown } from '../domain/scanEngine';
import { familyLabels } from '../domain/controls';
import { chartColors } from '../theme/brandTheme';
import {
  AZURE_CONTROLS, AZURE_FAMILY_ORDER, AZURE_FAMILY_COLORS, type AzureFamily,
} from '../domain/azureControls';
import { azureConnectionsApi } from '../api/azureConnectionsApi';
import { azureScansApi } from '../api/azureScansApi';
import type { Scan } from '../domain/types';

interface FamilyRow { key: string; label: string; score: number | null; color: string; }

function scoreColor(score: number | null): string {
  if (score == null) return tokens.colorNeutralForeground3;
  if (score >= 80) return '#1F7A45';
  if (score >= 60) return '#A86317';
  return '#B82329';
}

const M365_FAMILIES = ['identity', 'email', 'data', 'logs', 'endpoint', 'collab', 'apps'] as const;

export function DualScopeScore({ m365Scan }: { m365Scan: Scan | null }) {
  const { t } = useTranslation();
  const { m365: m365On, azure: azureOn } = useScanVisibility();

  // -------- M365 --------
  const m365 = useMemo(() => {
    if (!m365Scan) return null;
    const b = computeScoreBreakdown(m365Scan);
    const score = typeof m365Scan.globalScore === 'number' ? m365Scan.globalScore : b.globalScore;
    const weight = b.counts.pass + b.counts.warning + b.counts.fail;
    const families: FamilyRow[] = M365_FAMILIES.map((f, i) => ({
      key:   f,
      label: t(`family.${f}`, familyLabels[f]),
      score: b.byFamily[f],
      color: chartColors[i % chartColors.length],
    }));
    return { score, weight, families };
  }, [m365Scan, t]);

  // -------- Azure (fetch dernier scan de la 1ere connexion) --------
  const connQ = useQuery({
    queryKey: ['azure-connections'],
    queryFn:  () => azureConnectionsApi.list(),
    staleTime: 60_000, retry: false,
  });
  const connId = connQ.data?.connections?.[0]?.id;
  const azureScanQ = useQuery({
    queryKey: ['azure-latest', connId],
    queryFn:  () => azureScansApi.latest(connId!),
    enabled:  !!connId,
    staleTime: 10_000, retry: false,
  });

  const azure = useMemo(() => {
    const scan = azureScanQ.data;
    if (!scan || scan.status !== 'done' || (scan.results?.length ?? 0) === 0) return null;
    const byId = new Map(scan.results.map((r) => [r.controlId, r.status] as const));
    const agg: Record<AzureFamily, { total: number; pass: number }> = {
      identity: { total: 0, pass: 0 }, network: { total: 0, pass: 0 },
      data: { total: 0, pass: 0 },     compute: { total: 0, pass: 0 },
      keyvault: { total: 0, pass: 0 }, logging: { total: 0, pass: 0 },
      governance: { total: 0, pass: 0 },
    };
    let scored = 0, passed = 0;
    AZURE_CONTROLS.forEach((c) => {
      const st = byId.get(c.id);
      if (!st || st === 'manual' || st === 'not_applicable') return;
      agg[c.family].total++; scored++;
      if (st === 'pass') { agg[c.family].pass++; passed++; }
    });
    const score = typeof scan.globalScore === 'number' ? scan.globalScore
                : scored > 0 ? Math.round(100 * passed / scored) : 0;
    const families: FamilyRow[] = AZURE_FAMILY_ORDER.map((fam) => ({
      key:   fam,
      label: t(`nisAzure.family.${fam}`),
      score: agg[fam].total > 0 ? Math.round(100 * agg[fam].pass / agg[fam].total) : null,
      color: AZURE_FAMILY_COLORS[fam].fg,
    }));
    return { score, weight: scored, families };
  }, [azureScanQ.data, t]);

  // -------- Score global combine (moyenne ponderee) --------
  const showM365  = m365On  && !!m365;
  const showAzure = azureOn && !!azure;

  const global = useMemo(() => {
    const parts: Array<{ score: number; weight: number }> = [];
    if (showM365  && m365)  parts.push({ score: m365.score,  weight: Math.max(1, m365.weight) });
    if (showAzure && azure) parts.push({ score: azure.score, weight: Math.max(1, azure.weight) });
    if (parts.length === 0) return null;
    const wSum = parts.reduce((s, p) => s + p.weight, 0);
    return Math.round(parts.reduce((s, p) => s + p.score * p.weight, 0) / wSum);
  }, [showM365, showAzure, m365, azure]);

  if (!showM365 && !showAzure) return null;

  const radialData = [{ name: 'Score', value: global ?? 0, fill: scoreColor(global) }];

  return (
    <div>
      {/* ---- Score global combine ---- */}
      <Card style={{ marginBottom: 16, borderLeft: `4px solid ${scoreColor(global)}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 180px', alignItems: 'center', gap: 24, padding: '8px 4px' }}>
          <div>
            <Caption1 style={{ color: scoreColor(global), textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, fontSize: 12, display: 'block', marginBottom: 10 }}>
              {t('dualScore.title', 'Score global de conformité')}
            </Caption1>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
              {showM365 && m365 && <ScopeScore name="m365" label="Microsoft 365" score={m365.score} />}
              {showM365 && showAzure && <Operator symbol="+" />}
              {showAzure && azure && <ScopeScore name="azure" label="Microsoft Azure" score={azure.score} />}

              <Operator symbol="=" />

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Caption1 style={{ color: scoreColor(global), fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('dualScore.global', 'Total')}
                </Caption1>
                <div style={{ fontSize: 48, fontWeight: 700, lineHeight: 1.1, color: scoreColor(global), fontVariantNumeric: 'tabular-nums' }}>
                  {global == null ? '—' : global}
                  <span style={{ fontSize: 22, color: tokens.colorNeutralForeground3, fontWeight: 400 }}>/100</span>
                </div>
              </div>
            </div>

            {showM365 && showAzure && (
              <Caption1 style={{ display: 'block', marginTop: 10, color: tokens.colorNeutralForeground3, fontSize: 11 }}>
                {t('dualScore.hint', 'Moyenne pondérée par le nombre de contrôles évalués sur chaque périmètre (M365 + Azure).')}
              </Caption1>
            )}
          </div>

          <div style={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" barSize={16} data={radialData} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar background dataKey="value" cornerRadius={10} />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      {/* ---- Familles : 2 colonnes alignees sous chaque perimetre ---- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
        {showM365 && m365 && (
          <FamilyColumn name="m365" title={t('dualScore.familyM365', 'Score par famille — Microsoft 365')} families={m365.families} />
        )}
        {showAzure && azure && (
          <FamilyColumn name="azure" title={t('dualScore.familyAzure', 'Score par famille — Microsoft Azure')} families={azure.families} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ScopeScore({ name, label, score }: { name: 'm365' | 'azure'; label: string; score: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <BrandIcon name={name} size={14} />
        <Caption1 style={{ color: tokens.colorNeutralForeground3, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Caption1>
      </span>
      <div style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.1, color: scoreColor(score), fontVariantNumeric: 'tabular-nums' }}>
        {score}<span style={{ fontSize: 14, color: tokens.colorNeutralForeground3 }}>/100</span>
      </div>
    </div>
  );
}

function Operator({ symbol }: { symbol: string }) {
  return <div style={{ fontSize: 26, fontWeight: 300, color: tokens.colorNeutralForeground3, lineHeight: 1.1, paddingBottom: 4 }}>{symbol}</div>;
}

function FamilyColumn({ name, title, families }: { name: 'm365' | 'azure'; title: string; families: FamilyRow[] }) {
  return (
    <Card style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <BrandIcon name={name} size={20} />
        <Body1 style={{ fontWeight: 600 }}>{title}</Body1>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {families.map((f) => (
          <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 42px', alignItems: 'center', gap: 10 }}>
            <Caption1 style={{ color: tokens.colorNeutralForeground2, fontWeight: 500 }}>{f.label}</Caption1>
            <div style={{ height: 10, borderRadius: 5, background: tokens.colorNeutralBackground3, overflow: 'hidden' }}>
              <div style={{ width: `${f.score ?? 0}%`, height: '100%', background: f.color, transition: 'width 0.4s ease' }} />
            </div>
            <Body1 style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right', color: f.score == null ? tokens.colorNeutralForeground3 : tokens.colorNeutralForeground1 }}>
              {f.score == null ? '—' : f.score}
            </Body1>
          </div>
        ))}
      </div>
    </Card>
  );
}
